<template>
  <div>
    <ModalComponent :show="show" @close="attemptClose" class="cardinal-direction-dialog__modal">
      <CardV2 variant="prompt" :title="$t('setCardinalDirection')">
        <template #default>
          <div class="cardinal-direction-dialog__layout">
            <div class="cardinal-direction-dialog__stage">
              <img v-if="currentImage" :src="currentImage" :alt="`${current + 1}`" />
            </div>

            <div class="cardinal-direction-dialog__controls">
              <p class="cardinal-direction-dialog__help">{{ $t('chooseCardinalDirectionForFlatfinder') }}</p>
              <CompassDial :value="data[current]" :size="140" @input="degrees => $set(data, current, degrees)" />
              <InputComponent class="cardinal-direction-dialog__field">
                <template #default>
                  <span
                    class="cardinal-direction-dialog__value"
                    :class="{ 'cardinal-direction-dialog__value--empty': data[current] == null }"
                  >
                    {{ display || $t('cardinalDirectionPlaceholder') }}
                  </span>
                </template>
                <template #suffix>
                  <button
                    :disabled="data[current] == null"
                    type="button"
                    class="cardinal-direction-dialog__clear"
                    v-tooltip="data[current] == null ? '' : $t('cardinalDirectionClear')"
                    @click="$set(data, current, null)"
                  >
                    <Icon :icon="['fal', 'trash']" />
                  </button>
                </template>
              </InputComponent>
            </div>

            <FrameThumbnails
              class="cardinal-direction-dialog__thumbnails"
              :frames="frames"
              :current="current"
              @update:current="$emit('update:current', $event)"
            >
              <template #overlay="{ index }">
                <span
                  v-if="data[index] != null"
                  class="cardinal-direction-dialog__arrow overlay-icon"
                  :style="{ transform: `rotate(${data[index]}deg)` }"
                >
                  <Icon :icon="['fas', 'arrow-up']" />
                </span>
              </template>
              <template #caption="{ index }">
                <span class="cardinal-direction-dialog__degrees">{{ formatDegrees(data[index]) }}</span>
              </template>
            </FrameThumbnails>
          </div>
        </template>
        <template #actions>
          <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="attemptClose" />
          <ButtonComponent :label="$t('cardinalDirectionResetAll')" :icon="['fal', 'undo']" @click="showReset = true" />
          <ButtonComponent
            theme="primary"
            :class="{ 'elder-button--disabled': !hasChanges }"
            v-tooltip="hasChanges ? null : $t('noUnsavedChanges')"
            :label="$t('confirm')"
            :icon="['fal', 'arrow-to-bottom']"
            @click="save"
          />
        </template>
      </CardV2>
    </ModalComponent>

    <UnsavedChangesDialog :show="showDiscard" @confirm="discard" @cancel="showDiscard = false" />

    <ConfirmDialog
      :show="showReset"
      :title="$t('cardinalDirectionResetAll')"
      :message="$t('resetAllCardinalDirections')"
      :confirm-icon="['fal', 'undo']"
      @confirm="confirmReset"
      @cancel="showReset = false"
    />
  </div>
</template>

<script>
import { ButtonComponent } from 'vue-elder-button'
import { ModalComponent } from 'vue-elder-modal'
import { InputComponent } from 'vue-elder-input'
import CardV2 from '@/components/Card-v2'
import ConfirmDialog from '@/components/ConfirmDialog'
import UnsavedChangesDialog from '@/components/UnsavedChangesDialog'
import CompassDial from './CompassDial'
import FrameThumbnails from './FrameThumbnails'
import { directionPoint } from './directions'

export default {
  props: {
    show: Boolean,
    frames: {
      type: Array,
      default: () => [],
    },
    current: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      data: [],
      snapshot: '',
      showReset: false,
      showDiscard: false,
    }
  },
  watch: {
    show(open) {
      if (!open) return
      this.data = this.frames.map(frame => frame.cardinalDirection)
      this.snapshot = JSON.stringify(this.data)
    },
  },
  computed: {
    currentImage() {
      return this.$path('image.url', this.frames[this.current])
    },
    display() {
      const degrees = this.data[this.current]
      const point = directionPoint(degrees)
      if (!point) return
      return `${this.$t(`cardinalDirection${point}`)} · ${Math.round(degrees)}°`
    },
    hasChanges() {
      return JSON.stringify(this.data) !== this.snapshot
    },
  },
  methods: {
    formatDegrees(degrees) {
      if (degrees == null) return ''
      return `${Math.round(degrees)}°`
    },

    attemptClose() {
      if (this.showReset || this.showDiscard) return
      if (!this.hasChanges) return this.$emit('close')
      this.showDiscard = true
    },
    discard() {
      this.showDiscard = false
      this.$emit('close')
    },
    reset() {
      this.data = this.frames.map(() => null)
    },
    confirmReset() {
      this.reset()
      this.showReset = false
    },
    save() {
      if (!this.hasChanges) return
      this.frames.forEach((frame, index) => (frame.cardinalDirection = this.data[index]))
      this.$emit('close')
    },
  },
  components: {
    ButtonComponent,
    ModalComponent,
    InputComponent,
    CardV2,
    ConfirmDialog,
    CompassDial,
    FrameThumbnails,
    UnsavedChangesDialog,
  },
}
</script>

<style lang="scss">
.cardinal-direction-dialog {
  &__layout {
    display: grid;
    grid-template-columns: 1fr 250px;
    column-gap: var(--spacing-lg);
    align-items: center;
  }

  &__stage {
    grid-column: 1;
    grid-row: 1;

    img {
      width: 100%;
      border-radius: var(--border-radius-sm);
      display: block;
    }
  }

  &__controls {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  &__thumbnails {
    grid-column: 1;
    grid-row: 2;
  }

  &__help {
    margin: 0;
    text-align: center;
  }

  &__arrow {
    position: absolute;
    top: 4px;
    right: 4px;
    transition: transform 200ms ease;
  }

  &__degrees {
    min-height: 1.2em;
    line-height: 1.2;
    font-size: 0.75em;
    color: var(--dark-grey);
  }

  &__field {
    width: 100%;
  }

  &__value {
    text-align: center;

    display: flex;
    justify-content: center;
    align-items: center;

    font-size: 0.85em;

    &--empty {
      color: var(--dark-grey);
    }
  }

  &__clear {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: var(--dark-grey);
    cursor: pointer;

    &:not(:disabled):hover {
      color: var(--error);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.4;
    }
  }

  &__modal {
    .card-v2 {
      width: 1100px;
      max-width: 96vw;
    }

    .card-v2__actions {
      justify-content: flex-start;

      > :last-child {
        margin-left: auto;
      }
    }
  }
}
</style>
